import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { auth, crashlytics } from "../services/firebase";

const ProtectedRoute: React.FC = () => {
  const { isAuthenticated, isLoading, currentUser, isEmailVerified } =
    useAuth();
  const location = useLocation();
  const [verificationChecked, setVerificationChecked] = React.useState(false);

  // Inside ProtectedRoute
  React.useEffect(() => {
    let isMounted = true;
    const checkVerification = async () => {
      if (auth.currentUser && !verificationChecked) {
        try {
          await auth.currentUser.reload();
          if (isMounted) setVerificationChecked(true);
        } catch (err) {
          crashlytics.logError(
            `Error reloading user for email verification check. Error: ${JSON.stringify(err?.message)}`,
            "ProtectedRoute.tsx",
            err,
          );
        }
      }
    };
    checkVerification();
    return () => {
      isMounted = false;
    }; // Cleanup for React 19 strict mode
  }, [auth.currentUser, verificationChecked]);

  // Show loading spinner while authenticating
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary"></div>
      </div>
    );
  }

  // Only redirect if we're sure user is not authenticated
  if (!isAuthenticated && !isLoading) {
    // Store the attempted location
    const currentPath = location.pathname;
    if (
      currentPath !== "/login" &&
      currentPath !== "/signup" &&
      currentPath !== "/welcome"
    ) {
      return <Navigate to="/welcome" state={{ from: location }} replace />;
    }
    return <Navigate to="/welcome" replace />;
  }

  // Check email verification for email/password users (Google users are pre-verified)
  // Check if user has password provider (meaning they signed up with email/password)
  const hasPasswordProvider = auth.currentUser?.providerData.some(
    (provider) => provider.providerId === "password",
  );

  if (
    currentUser &&
    hasPasswordProvider &&
    !isEmailVerified() &&
    location.pathname !== "/verify-email"
  ) {
    return <Navigate to="/verify-email" replace />;
  }

  // If user is authenticated but hasn't completed onboarding
  // Allow them to access profile-builder and onboarding pages
  if (
    currentUser &&
    isEmailVerified() &&
    !currentUser.hasCompletedOnboarding &&
    location.pathname !== "/onboarding" &&
    location.pathname !== "/profile-builder"
  ) {
    return <Navigate to="/profile-builder" replace />;
  }

  // Allow users to revisit onboarding page even after completion
  // (useful for reviewing app features or restarting onboarding)

  // If authenticated and has completed onboarding, render the protected content
  return <Outlet />;
};

export default ProtectedRoute;
